import * as React from "react";
import { useCallback } from "react";

export const CellRender = React.memo((props) => {
  const dataItem = props.originalProps.dataItem;
  const cellField = props.originalProps.field;
  const inEditField = dataItem[props.editField || ""];
  const objCancelBtn = props.cancelActBtn ? document.getElementById(props.cancelActBtn) : null;
  const objUpdateBtn = props.updateActBtn ? document.getElementById(props.updateActBtn) : null;

  const additionalProps =
    cellField && cellField === inEditField
      ? {
          ref: (td) => {
            //set input
            let input = null;
            if (td) {
              input = td.querySelector("input");

              if (input === null) {
                input = td.querySelector("select");
              }
              if (input === null) {
                input = td.querySelector("textarea");
              }
            }

            if (!input) {
              return;
            }

            objCancelBtn && objCancelBtn.enableKendoBtn();
          },
          onChange: () => {
            objUpdateBtn && objUpdateBtn.enableKendoBtn();
            window.$_UNSAVED_CHANGES_FLAG = true;
          },
          onKeyDown: (e) => {
            if (objCancelBtn && e.keyCode === 27) {
              const timeout = setTimeout(function () {
                objCancelBtn.click();
              });
              return () => clearTimeout(timeout); // Clean up timeout
            }
          },
          title: props?.title || "",
          tabIndex: props.gridData ? "0" : null,
        }
      : {
          onDoubleClick: (e) => {
            if (!props.enterEdit) return false;
            let currentDataItem = props.originalProps.dataItem;
            let currentCellField = props.originalProps.field;
            // Always use primary_key to look up the correct data item
            if (props.gridData && currentDataItem && currentDataItem.primary_key) {
              const found = props.gridData.find((item) => item.primary_key === currentDataItem.primary_key);
              if (found) currentDataItem = found;
            }
            props.enterEdit(currentDataItem, currentCellField);
            // Focus input if present
            const td = e.target.closest("td");
            const timeout = setTimeout(function () {
              let input = td ? td.querySelector("input") : null;
              if (input === null && td) input = td.querySelector("select");
              if (input !== null) input.focus({ preventScroll: true });
            });
            return () => clearTimeout(timeout); // Clean up timeout
          },
          onClick: (e) => {
            let currentDataItem = props.originalProps.dataItem;
            let currentCellField = props.originalProps.field;
            if (props.gridData && currentDataItem && currentDataItem.primary_key) {
              const found = props.gridData.find((item) => item.primary_key === currentDataItem.primary_key);
              if (found) currentDataItem = found;
            }
            props?.onRowClick && props?.onRowClick(currentDataItem, e, currentCellField);
          },
          onFocus: (e) => {
            if (inEditField) {
              let currentDataItem = props.originalProps.dataItem;
              let currentCellField = props.originalProps.field;
              if (props.gridData && currentDataItem && currentDataItem.primary_key) {
                const found = props.gridData.find((item) => item.primary_key === currentDataItem.primary_key);
                if (found) currentDataItem = found;
              }
              props.enterEdit(currentDataItem, currentCellField);
              const td = e.target.closest("td");
              let input = td ? td.querySelector("input") : null;
              if (input === null && td) input = td.querySelector("select");
              if (input !== null) input.focus({ preventScroll: true });
            }
          },
          tabIndex: "0",
          title: props?.title || "",
        };

  const className = props.hasCopiedFlag ? `${props.td.props.className || ""} dirty-l-cell`.trim() : "";

  const clonedProps = { ...props.td.props, ...additionalProps };
  if (className) clonedProps.className = className;
  return React.cloneElement(props.td, clonedProps, props.td.props.children);
});

export const RowRender = React.memo((props) => {
  const trProps = {
    ...props.tr.props,
    title: props?.title || "",
    onBlur: (e) => {
      props?.exitEdit &&
        props?.exitEdit(
          props.originalProps.dataItem,
          props.tr.props["data-grid-row-index"],
          props.originalProps.dataIndex,
          e
        );
    },
    onFocus: (e) => {},
    onDoubleClick: (e) => {
      if (props?.onRowDoubleClick) {
        props.onRowDoubleClick(props);
      }
    },
    onClick: (e) => {
      if (props?.onRowClick) {
        props.onRowClick(props, e);
      }
    },
  };

  return React.cloneElement(props.tr, { ...trProps }, props.tr.props.children);
});

export const EventRowRender = React.memo((props) => {
  // Memoized onBlur handler
  const handleBlur = useCallback(
    (e) => {
      if (props.exitEdit) {
        props.exitEdit(
          props.originalProps.dataItem,
          props.tr.props["data-grid-row-index"],
          props.originalProps.dataIndex,
          e
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [props.exitEdit, props.originalProps.dataItem, props.tr.props, props.originalProps.dataIndex]
  );

  // Memoized onClick handler
  const handleClick = useCallback(
    (e) => {
      if (props.onRowClick) {
        props.onRowClick(props, e);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [props.onRowClick, props]
  );

  // Create new trProps object
  const trProps = {
    ...props.tr.props,
    onClick: handleClick,
  };

  if (props.exitEdit) {
    trProps.onBlur = handleBlur;
  }

  return React.cloneElement(props.tr, { ...trProps }, props.tr.props.children);
});

export const ProviderRowRender = React.memo((props) => {
  // Memoized onBlur handler
  const handleBlur = useCallback(
    (e) => {
      if (props.exitEdit) {
        props.exitEdit(
          props.originalProps.dataItem,
          props.tr.props["data-grid-row-index"],
          props.originalProps.dataIndex,
          e
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [props.exitEdit, props.originalProps.dataItem, props.tr.props, props.originalProps.dataIndex]
  );

  // Memoized onDoubleClick handler
  const handleDoubleClick = useCallback(
    (e) => {
      if (props.onRowDoubleClick) {
        props.onRowDoubleClick(props);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [props.onRowDoubleClick, props]
  );

  // Memoized onClick handler
  const handleClick = useCallback(
    (e) => {
      if (props.onRowClick) {
        props.onRowClick(props, e);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [props.onRowClick, props]
  );

  // Create new trProps object
  const trProps = {
    ...props.tr.props,
    title: props.title || "",
    onFocus: () => {},
    onDoubleClick: handleDoubleClick,
    onClick: handleClick,
  };

  if (props.exitEdit) {
    trProps.onBlur = handleBlur;
  }

  return React.cloneElement(props.tr, { ...trProps }, props.tr.props.children);
});
